// src/components/Table/TableBody.tsx

import React from 'react';
import { TableBody as StyledTableBody } from './styles';
import { TableRow } from './TableRow';
import { TableCell } from './TableCell';
import { Column, ActionColumn } from '../../types/table';
import { SkeletonCell } from '../SkeletonCell';

interface TableBodyProps<T> {
  columns: Array<Column<T> | ActionColumn<T>>;
  data: T[];
  showActions?: boolean;
  onRowClick?: (record: T) => void;
  emptyMessage?: string;
  loading?: boolean;
  rowClickable?: boolean;
}

export const TableBody = <T extends { id?: string | number }>({
  columns,
  data,
  showActions = false,
  onRowClick,
  emptyMessage = 'No data available',
  loading = false,
  rowClickable = false
}: TableBodyProps<T>) => {
  const skeletonRows = 5;

  if (loading) {
    return (
      <StyledTableBody>
        {Array.from({ length: skeletonRows }).map((_, idx) => (
          <TableRow key={`skeleton-${idx}`} clickable={false}>
            {columns.map((column) => {
              if (column.key === 'actions' && !showActions) return null;
              return (
                <TableCell key={`${String(column.key)}-skeleton-${idx}`} align={column.align}>
                  <SkeletonCell height="14px" width="80%" />
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </StyledTableBody>
    );
  }

  return (
    <StyledTableBody>
      {data.map((record, index) => (
        <TableRow 
          key={record.id || index} 
          clickable={!!onRowClick || rowClickable}
          onClick={() => onRowClick?.(record)}
        >
          {columns.map((column) => {
            if (column.key === 'actions' && !showActions) return null;

            if (column.key === 'actions') {
              const actionColumn = column as ActionColumn<T>;
              return (
                <TableCell key={column.key as string} align={column.align}>
                  {actionColumn.actions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(record);
                      }}
                      disabled={action.disabled?.(record)}
                      style={{
                        marginRight: '8px',
                        padding: '4px 12px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: action.disabled?.(record) ? 'not-allowed' : 'pointer',
                        background: action.variant === 'danger' ? '#fee2e2' : '#dbeafe',
                        color: action.variant === 'danger' ? '#991b1b' : '#1e40af',
                        fontSize: '13px',
                        fontWeight: 500
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                </TableCell>
              );
            }

            const isActionColumn = column.key === 'actions';
            if (isActionColumn) return null;
            
            const colKey = column.key as string;
            const value = typeof colKey === 'string' && colKey.includes('.')
              ? colKey.split('.').reduce((obj, key) => obj?.[key], record as any)
              : record[column.key as keyof T];

            const dataColumn = column as Column<T>;
            return (
              <TableCell key={colKey} align={column.align}>
                {dataColumn.render ? dataColumn.render(value, record) : value}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </StyledTableBody>
  );
};